import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { DOORWAYS } from "../../data/homeContent";

const KEY = "gme_discovery_seen";

// Six Doorways arranged clockwise on a hexagon (60° steps), starting at top.
const ANGLES = [0, 60, 120, 180, 240, 300];
const C = 200;          // svg center
const R_POINT = 128;    // radius of the outer doorway circles
const VIEW = 400;

const pt = (deg, r) => {
  const t = (deg * Math.PI) / 180;
  return { x: C + r * Math.sin(t), y: C - r * Math.cos(t) };
};

// Positions (as % of the square constellation container) for each doorway circle.
const NODES = DOORWAYS.slice(0, 6).map((g, i) => {
  const p = pt(ANGLES[i], R_POINT);
  return { ...g, left: (p.x / VIEW) * 100, top: (p.y / VIEW) * 100 };
});

// Outer polygon vertices (svg coords) for the connecting constellation path.
const ORDERED = ANGLES.map((a) => pt(a, R_POINT)); // clockwise
const POLY = ORDERED.map((p) => `${p.x},${p.y}`).join(" ");

// Little star dots scattered along each edge of the constellation path.
const DOTS = [];
for (let i = 0; i < ORDERED.length; i++) {
  const a = ORDERED[i];
  const b = ORDERED[(i + 1) % ORDERED.length];
  for (let k = 1; k <= 3; k++) {
    const f = k / 4;
    DOTS.push({ x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, r: k === 2 ? 2.4 : 1.4 });
  }
}

// Flower-of-life sacred geometry (center + 6 petals), inner radius.
const FLOWER_R = 46;
const FLOWER = [{ x: C, y: C }, ...[0, 60, 120, 180, 240, 300].map((a) => pt(a, FLOWER_R))];

// Restrained MEA-style discovery prompt. Triggers once per session after ~38% scroll.
// Every door opens the Starting Point selector (pre-selecting that doorway).
export const DiscoveryPopup = ({ onFind }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(KEY)) return;
    const onScroll = () => {
      const h = document.documentElement;
      const pct = h.scrollTop / (h.scrollHeight - h.clientHeight || 1);
      if (pct >= 0.38) {
        setOpen(true);
        sessionStorage.setItem(KEY, "1");
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);
  const pick = (id) => { close(); setTimeout(() => onFind?.(id), 250); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[130] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm"
          onClick={close} data-testid="discovery-popup">
          <motion.div initial={{ y: 40, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-3xl bg-[#070B16] ring-gold-frame grain">
            {/* aurora-weave deliberately bleeds 20% past its own box (for a soft glow
                past the edges) and is separately keyframe-animated up to scale(1.25) —
                it must stay inside an overflow-hidden layer of its own. Putting it in
                the same scrollable box as the content below made the browser count its
                animated, bleeding bounds as scrollable overflow, so the scrollbar grew
                and shrank in lockstep with the animation with no user action at all. */}
            <div className="aurora-weave slow" />
            <button data-testid="discovery-close" onClick={close}
              className="absolute top-4 right-4 z-20 text-cream/60 hover:text-[#E7CC86] transition-colors"><X size={22} /></button>

            <div className="relative z-10 max-h-[92vh] overflow-y-auto px-5 sm:px-8 pt-6 pb-5">
              {/* Heading */}
              <h3 className="font-display font-bold text-3xl sm:text-4xl text-cream leading-tight text-center">
                Where does this work <span className="text-gold">meet you?</span>
              </h3>
              <div className="flex items-center justify-center gap-3 my-2.5">
                <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#C9A24B]/60" />
                <span className="h-1.5 w-1.5 rotate-45 bg-[#E7CC86]" />
                <span className="h-px w-10 bg-gradient-to-l from-transparent to-[#C9A24B]/60" />
              </div>
              <p className="text-cream/70 text-center text-[15px] sm:text-base mb-1.5">
                Find the doorway that speaks to <span className="text-gold">where you are now.</span>
              </p>

              {/* Constellation */}
              <div className="relative w-full max-w-[360px] mx-auto aspect-square">
                <motion.svg viewBox={`0 0 ${VIEW} ${VIEW}`} className="absolute inset-0 w-full h-full"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.15 }}>
                  <defs>
                    <radialGradient id="starGlow" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#F4E4B0" stopOpacity="0.9" />
                      <stop offset="35%" stopColor="#E7CC86" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#E7CC86" stopOpacity="0" />
                    </radialGradient>
                  </defs>

                  {/* Sacred geometry: flower of life */}
                  {FLOWER.map((f, i) => (
                    <circle key={i} cx={f.x} cy={f.y} r={FLOWER_R} fill="none"
                      stroke="#C9A24B" strokeOpacity="0.14" strokeWidth="0.8" />
                  ))}
                  <circle cx={C} cy={C} r={FLOWER_R * 2} fill="none" stroke="#C9A24B" strokeOpacity="0.1" strokeWidth="0.8" />

                  {/* Constellation connecting path */}
                  <polygon points={POLY} fill="none" stroke="#C9A24B" strokeOpacity="0.45" strokeWidth="1" />
                  {DOTS.map((d, i) => (
                    <circle key={`d${i}`} cx={d.x} cy={d.y} r={d.r} fill="#E7CC86" fillOpacity="0.85" />
                  ))}

                  {/* Central star */}
                  <circle cx={C} cy={C} r={60} fill="url(#starGlow)" />
                  <g stroke="#F4E4B0" strokeWidth="1.4" strokeLinecap="round">
                    <line x1={C} y1={C - 22} x2={C} y2={C + 22} />
                    <line x1={C - 22} y1={C} x2={C + 22} y2={C} />
                    <line x1={C - 13} y1={C - 13} x2={C + 13} y2={C + 13} strokeWidth="0.9" strokeOpacity="0.7" />
                    <line x1={C - 13} y1={C + 13} x2={C + 13} y2={C - 13} strokeWidth="0.9" strokeOpacity="0.7" />
                  </g>
                  <circle cx={C} cy={C} r={3.2} fill="#FFF6DA" />
                </motion.svg>

                {/* Doorway nodes — circle centers sit exactly on the hexagon vertices */}
                <motion.div className="absolute inset-0" style={{ transformOrigin: "50% 50%" }}
                  initial={{ rotate: -28 }} animate={{ rotate: 0 }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}>
                  {NODES.map((g, i) => (
                    <div key={g.id} className="absolute -translate-x-1/2 -translate-y-1/2"
                      style={{ left: `${g.left}%`, top: `${g.top}%` }}>
                      <motion.button
                        data-testid={`discovery-door-${g.n}`}
                        onClick={() => pick(g.id)}
                        aria-label={`Open the ${g.title} doorway`}
                        className="group relative block focus:outline-none"
                        style={{ transformOrigin: "50% 50%" }}
                        initial={{ opacity: 0, scale: 0.2 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.45 + i * 0.12, duration: 0.55, ease: [0.34, 1.56, 0.64, 1] }}
                      >
                        {/* §14 iconography — the doorway's symbol, not stock photography. */}
                        <span className="grid place-items-center w-[52px] h-[52px] sm:w-[68px] sm:h-[68px] rounded-full bg-[#0B1020] ring-2 ring-[#C9A24B]/70 shadow-[0_0_20px_rgba(201,162,75,0.25)] transition-all duration-300 group-hover:ring-[#E7CC86] group-hover:shadow-[0_0_28px_rgba(231,204,134,0.55)] group-hover:scale-105">
                          <g.icon size={26} strokeWidth={1.5} className="text-[#E7CC86]" aria-hidden="true" />
                        </span>
                        <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 w-28 text-center text-[10px] sm:text-[11px] leading-tight font-display text-cream/90 group-hover:text-gold transition-colors">
                          {g.title}
                        </span>
                      </motion.button>
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* CTA */}
              <button data-testid="discovery-find" onClick={() => { close(); setTimeout(() => onFind?.(), 250); }}
                className="btn-gold w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 mt-2">
                Find My Starting Point <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
